import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Placeholder } from "@bloomberg/media-ui-article";
import { ScreenContext } from "@foundation/screen";
import { createClasses, Tab, Tabs } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import {
  TickerChart,
  type TickerChartData,
  type TickerChartDataPoint,
} from "@bloomberg/media-ui-markets";
import type { WatchlistComparison } from "@foundation/watchlist-client";
import { Refresh } from "@bloomberg/media-ui-icons";
import { useSessionStorage } from "@foundation/local-storage";
import styles from "./styles.module.css";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import { LOADING_STATE, WATCHLIST_FAILURE_TEXT } from "~constants/Portfolio";
import {
  fetchWatchlistComparisonChartData,
  filterSelectedTickers,
  getChartDimensions,
} from "~components/WatchlistComparisonChart/utils";
import type {
  PortfolioChartDateRange,
  WatchlistComparisonChartData,
} from "~types/portfolio";
import { TickerChartLegendData } from "~types/types";
import Notification from "~components/Notification";

function ChartLoadingState() {
  return (
    <div className="w-full">
      <Placeholder>
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
      </Placeholder>
    </div>
  );
}

export default function WatchlistComparisonChart(
  props: PropsWithChildren<{
    handleChartInteraction?: (chartDataPoint: TickerChartDataPoint) => void;
    tickerChartLegends: Array<TickerChartLegendData>;
    watchlistComparisons: Array<WatchlistComparison>;
    isMaximized: boolean;
  }>
) {
  const {
    children,
    handleChartInteraction,
    tickerChartLegends,
    watchlistComparisons,
    isMaximized,
  } = props;
  const { selectedPortfolio } = usePortfolioPage();

  const prevPortfolioId = useRef(selectedPortfolio?.portInfo?.id);

  const apiCalled = useRef(false);

  const [tickerChartData, setTickerChartData] = useState<
    Array<WatchlistComparisonChartData>
  >([]);

  const [chartDataMap, setChartDataMap] = useState<
    Map<string, Array<WatchlistComparisonChartData>>
  >(new Map());

  const [selectedTab, setSelectedTab] = useSessionStorage(
    "compChartDateRange",
    "1M"
  );

  const [loadingState, setLoadingState] = useState(LOADING_STATE.EMPTY);

  const {
    breakpoints: { tablet, smallDesktop, extraSmallDesktop, largeDesktop },
  } = useContext(ScreenContext);

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    trackClickEvent(
      "chartHover",
      "hover_comparison_chart",
      "uiaction",
      "Market Data"
    );
  };

  const trackComparisonChartTabClick = useCallback((tabTitle: string) => {
    trackClickEvent(
      `chart_${tabTitle}`,
      `chart_${tabTitle}`,
      "click",
      "engagement"
    );

    // update selected tab
    setSelectedTab(tabTitle);
  }, []);

  const refreshWatchlistChart = useCallback(() => {
    if (!selectedPortfolio?.positions?.length || !watchlistComparisons.length) {
      return;
    }

    // skip refresh when in-progress
    if (apiCalled.current) return;

    // set loading state
    apiCalled.current = true;
    setLoadingState(LOADING_STATE.LOADING);

    fetchWatchlistComparisonChartData(
      selectedTab as PortfolioChartDateRange,
      selectedPortfolio,
      watchlistComparisons
    )
      .then((chartData) => {
        // validate if chart data is available
        if (!chartData?.length) {
          // reset api called status
          apiCalled.current = false;
          setLoadingState(LOADING_STATE.FAILED);

          return;
        }

        // update parsed api response
        setChartDataMap((prevState) => {
          prevState.set(selectedTab, chartData);

          return prevState;
        });

        // filter user selected tickers
        setTickerChartData(
          filterSelectedTickers(tickerChartLegends, chartData)
        );

        // reset api called status
        apiCalled.current = false;
        setLoadingState(LOADING_STATE.COMPLETE);
      })
      .catch((_) => {
        // reset api called status
        apiCalled.current = false;
        setLoadingState(LOADING_STATE.FAILED);
      });
  }, [
    selectedPortfolio,
    selectedTab,
    tickerChartLegends,
    watchlistComparisons,
  ]);

  // fetch watchlist chart data on tab selection change
  // NOTE: do not include the tickerChartLegends in dependency
  useEffect(() => {
    // selected tab data is available, refresh not required
    if (chartDataMap.has(selectedTab)) {
      // filter user selected tickers
      setTickerChartData(
        filterSelectedTickers(
          tickerChartLegends,
          chartDataMap.get(selectedTab) as Array<WatchlistComparisonChartData>
        )
      );

      return;
    }

    refreshWatchlistChart();
  }, [chartDataMap, selectedTab]);

  // refresh comparison chart data on tickerChartLegend selection change
  useEffect(() => {
    if (!tickerChartLegends?.length) {
      return;
    }

    // filter user selected tickers
    setTickerChartData(
      filterSelectedTickers(
        tickerChartLegends,
        chartDataMap.get(selectedTab) as Array<WatchlistComparisonChartData>
      )
    );
  }, [tickerChartLegends]);

  // refresh comparison chart when comparisons are updated
  useEffect(() => {
    if (!watchlistComparisons?.length) {
      return;
    }

    const tickerIdSet = new Set<string>();

    // prepare a set of unique ticker id
    watchlistComparisons.forEach((comparison) => {
      tickerIdSet.add(comparison.watchlistId.toString());

      if (comparison.comparisonTicker) {
        tickerIdSet.add(comparison.comparisonTicker);
      } else if (comparison.comparisonWatchlistId) {
        tickerIdSet.add(comparison.comparisonWatchlistId.toString());
      }
    });

    // check if any new ticker has been added to comparison
    for (const tickerId of tickerIdSet) {
      // check if ticker exists
      const tickerData = tickerChartData.find(
        (chartData) => chartData.id.toString() === tickerId
      );

      // trigger refresh when new tickers added
      if (!tickerData) {
        // clear chart data map before refreshing
        // new tickers might not be available in map for selected tab
        setChartDataMap(new Map());

        refreshWatchlistChart();

        return;
      }
    }

    // at this point, we know all tickers are available in chart
    // do nothing
  }, [watchlistComparisons]);

  // reset chart data and refresh comparison chart data on watchlist save
  useEffect(() => {
    // Do not refresh chart on watchlist selection change
    if (selectedPortfolio?.portInfo?.id !== prevPortfolioId.current) {
      prevPortfolioId.current = selectedPortfolio?.portInfo?.id;

      return;
    }

    // clear chart data map before refreshing
    // new tickers might not be available in map for selected tab
    setChartDataMap(new Map());

    refreshWatchlistChart();
  }, [selectedPortfolio?.positions, selectedPortfolio?.portInfo]);

  // calculate x-axis interval based on the chart date range selected
  const xAxisInterval = useMemo(() => {
    if (selectedTab === "5Y") {
      return "month";
    }

    return selectedTab === "1D" ? "hour" : "day";
  }, [selectedTab]);

  // get chart dimensions according to breakpoint
  const { chartWidth, chartHeight } = useMemo(() => {
    return getChartDimensions(
      tablet,
      extraSmallDesktop,
      smallDesktop,
      largeDesktop,
      isMaximized
    );
  }, [tablet, extraSmallDesktop, smallDesktop, largeDesktop, isMaximized]);

  // compute chart class to handle intraday overlay
  const chartClass = createClasses({
    [styles.oneDayChart]: selectedTab === "1D",
  });

  if (loadingState === LOADING_STATE.LOADING) {
    return (
      <div className={styles.comparisonChartContainer}>
        <ChartLoadingState />
      </div>
    );
  }

  if (loadingState === LOADING_STATE.FAILED) {
    return (
      <div className={styles.comparisonChartContainer}>
        <Notification
          text={WATCHLIST_FAILURE_TEXT}
          variant="lineBreak"
          title=""
          actionIcon={<Refresh />}
          actionText="Refresh"
          onActionClick={refreshWatchlistChart}
        />
      </div>
    );
  }

  return (
    <div className={styles.comparisonChartContainer}>
      <Tabs
        className={styles.tabs}
        size={largeDesktop ? "default" : "small"}
        variant="gliding"
        onTabSwitch={trackComparisonChartTabClick}
      >
        {["1M", "6M", "YTD", "1Y", "5Y"].map((chartType) => (
          <Tab
            key={chartType}
            title={chartType}
            isActive={selectedTab === chartType}
            isDefault={selectedTab === chartType}
          >
            <></>
          </Tab>
        ))}
      </Tabs>

      {/* Render Ticker Chart Legends */}
      {children}
      <div onMouseLeave={(evt) => handleMouseLeave(evt)}>
        <TickerChart
          className={chartClass}
          tickerSnapshots={tickerChartData as Array<TickerChartData>}
          renderMode={tickerChartData.length === 1 ? "price" : "percentage"}
          showGradient={tickerChartData.length === 1}
          pricePrecision={2}
          xAxisInterval={xAxisInterval}
          enableDragInteraction={isMaximized}
          handleChartHoverInteraction={handleChartInteraction}
          width={chartWidth}
          height={chartHeight}
        />
      </div>
    </div>
  );
}
